import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogFormGrid, DialogFormSection,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { eduContractsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../lib/rbac';
import { FileSignature, Plus, Pencil, Trash2, Loader2, RefreshCw, CalendarClock, FileText } from 'lucide-react';
import Swal from 'sweetalert2';
import { EmptyState } from './common/States';
import { StatCard } from './common/StatCard';

const CONTRACT_TYPES = ['semester', 'academic_year', 'visiting', 'adjunct'];
const TYPE_LABEL = {
  semester: 'Semester', academic_year: 'Academic Year', visiting: 'Visiting', adjunct: 'Adjunct',
};
const STATUSES = ['active', 'expired', 'renewed', 'terminated'];
const STATUS_LABEL = {
  active: 'Active', expired: 'Expired', renewed: 'Renewed', terminated: 'Terminated',
};

const EMPTY_FORM = {
  faculty_profile_id: '', employee_id: '', contract_type: 'semester',
  starts_on: '', ends_on: '', auto_renew: false, hourly_rate: '',
  status: 'active', renewal_state: 'none', esign_status: 'unsigned',
  title: '', notes: '',
};

const EXPIRING_DAYS = 60;

const toArray = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.items || d?.results || [];
};

const EducationContractsManagement = () => {
  const { user } = useAuth();
  const canManage = Boolean(user?.isSuperadmin) || hasAnyRole(user?.role, ['admin', 'workspace_admin', 'hr_admin']);

  const [loading, setLoading] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [expiring, setExpiring] = useState([]);

  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [expiringOnly, setExpiringOnly] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.contract_type = typeFilter;
      const [listRes, expRes] = await Promise.all([
        eduContractsAPI.list(params),
        eduContractsAPI.expiring(EXPIRING_DAYS),
      ]);
      setContracts(toArray(listRes));
      setExpiring(toArray(expRes));
    } catch (err) {
      if (err?.response?.status === 402) {
        setContracts([]);
        setExpiring([]);
      } else {
        Swal.fire('Could not load contracts', err?.response?.data?.detail || 'Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run on filter change; load is stable
  useEffect(() => { load(); }, [statusFilter, typeFilter]);

  const stats = useMemo(() => {
    const total = contracts.length;
    const active = contracts.filter((c) => c.status === 'active').length;
    const renewals = contracts.filter((c) => c.renewal_state === 'pending').length;
    return { total, active, renewals, expiring: expiring.length };
  }, [contracts, expiring]);

  const expiringIds = useMemo(() => new Set(expiring.map((c) => c.id)), [expiring]);

  const rows = useMemo(() => (
    expiringOnly ? contracts.filter((c) => expiringIds.has(c.id)) : contracts
  ), [contracts, expiringOnly, expiringIds]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      ...EMPTY_FORM,
      ...row,
      faculty_profile_id: row.faculty_profile_id != null ? String(row.faculty_profile_id) : '',
      employee_id: row.employee_id != null ? String(row.employee_id) : '',
      hourly_rate: row.hourly_rate != null ? String(row.hourly_rate) : '',
      starts_on: row.starts_on || '',
      ends_on: row.ends_on || '',
    });
    setDialogOpen(true);
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async () => {
    if (!form.contract_type) return Swal.fire('Type required', 'Select a contract type.', 'warning');
    const payload = {
      contract_type: form.contract_type,
      faculty_profile_id: form.faculty_profile_id ? Number(form.faculty_profile_id) : null,
      employee_id: form.employee_id ? Number(form.employee_id) : null,
      starts_on: form.starts_on || null,
      ends_on: form.ends_on || null,
      auto_renew: Boolean(form.auto_renew),
      hourly_rate: form.hourly_rate !== '' ? Number(form.hourly_rate) : null,
      status: form.status,
      renewal_state: form.renewal_state,
      esign_status: form.esign_status,
      title: form.title || null,
      notes: form.notes || null,
    };
    try {
      if (editingId) {
        await eduContractsAPI.update(editingId, payload);
      } else {
        await eduContractsAPI.create(payload);
      }
      setDialogOpen(false);
      await load();
      Swal.fire({ icon: 'success', title: editingId ? 'Contract updated' : 'Contract added', timer: 1400, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Save failed', err?.response?.data?.detail || 'Please review the form and try again.', 'error');
    }
  };

  const renew = async (row) => {
    const ok = await Swal.fire({
      icon: 'question', title: 'Renew this contract?',
      text: row.auto_renew ? 'Auto-renew is on — the term will be extended.' : 'The contract will be marked renewed.',
      showCancelButton: true, confirmButtonText: 'Renew',
    });
    if (!ok.isConfirmed) return;
    try {
      await eduContractsAPI.renew(row.id);
      await load();
      Swal.fire({ icon: 'success', title: 'Contract renewed', timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Renew failed', err?.response?.data?.detail || 'Please try again.', 'error');
    }
  };

  const remove = async (row) => {
    const ok = await Swal.fire({
      icon: 'warning', title: 'Delete contract?',
      text: `${row.title || `Contract #${row.id}`} will be removed.`,
      showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#b5456b',
    });
    if (!ok.isConfirmed) return;
    try {
      await eduContractsAPI.remove(row.id);
      await load();
      Swal.fire({ icon: 'success', title: 'Deleted', timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Delete failed', err?.response?.data?.detail || 'Please try again.', 'error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <FileSignature className="h-6 w-6 text-indigo-600" /> Faculty Contracts
          </h2>
          <p className="text-sm text-muted-foreground">
            Term and engagement contracts for faculty — renewals, e-sign status and expiry tracking.
          </p>
        </div>
        {canManage && (
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1" /> Add Contract
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Contracts" value={stats.total} icon={FileText} />
        <StatCard label="Active" value={stats.active} icon={FileSignature} />
        <StatCard label="Pending Renewal" value={stats.renewals} icon={RefreshCw} />
        <StatCard label={`Expiring (${EXPIRING_DAYS}d)`} value={stats.expiring} icon={CalendarClock} />
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>Contracts</CardTitle>
              <CardDescription>All faculty contracts in this institute.</CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-40"><SelectValue placeholder="All statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={typeFilter || 'all'} onValueChange={(v) => setTypeFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-40"><SelectValue placeholder="All types" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {CONTRACT_TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_LABEL[t]}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button
                variant={expiringOnly ? 'default' : 'outline'}
                onClick={() => setExpiringOnly((v) => !v)}
              >
                <CalendarClock className="h-4 w-4 mr-1" /> Expiring soon
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading contracts…
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={FileSignature}
              title="No contracts found"
              description={canManage ? 'Add a contract to get started.' : 'No contracts have been added yet.'}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Starts</TableHead>
                    <TableHead>Ends</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Renewal</TableHead>
                    <TableHead>E-sign</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">
                        {row.title || `#${row.id}`}
                        {expiringIds.has(row.id) && <Badge variant="outline" className="ml-2 text-amber-600 border-amber-300">Expiring</Badge>}
                      </TableCell>
                      <TableCell><Badge variant="secondary">{TYPE_LABEL[row.contract_type] || row.contract_type || '—'}</Badge></TableCell>
                      <TableCell>{row.starts_on || '—'}</TableCell>
                      <TableCell>{row.ends_on || '—'}</TableCell>
                      <TableCell><Badge variant="secondary">{STATUS_LABEL[row.status] || row.status}</Badge></TableCell>
                      <TableCell>{row.renewal_state || '—'}</TableCell>
                      <TableCell>{row.esign_status || '—'}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {canManage && <Button variant="ghost" size="icon" onClick={() => renew(row)} title="Renew"><RefreshCw className="h-4 w-4" /></Button>}
                        {canManage && <Button variant="ghost" size="icon" onClick={() => openEdit(row)} title="Edit"><Pencil className="h-4 w-4" /></Button>}
                        {canManage && <Button variant="ghost" size="icon" onClick={() => remove(row)} title="Delete"><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Contract' : 'Add Contract'}</DialogTitle>
          </DialogHeader>
          <DialogFormSection>
            <DialogFormGrid>
              <div className="space-y-1">
                <Label>Title</Label>
                <Input value={form.title || ''} onChange={(e) => setField('title', e.target.value)} placeholder="Semester 2026 — Dr. Rao" />
              </div>
              <div className="space-y-1">
                <Label>Contract Type</Label>
                <Select value={form.contract_type} onValueChange={(v) => setField('contract_type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONTRACT_TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_LABEL[t]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Faculty Profile ID</Label>
                <Input type="number" value={form.faculty_profile_id} onChange={(e) => setField('faculty_profile_id', e.target.value)} placeholder="e.g. 12" />
              </div>
              <div className="space-y-1">
                <Label>Employee ID</Label>
                <Input type="number" value={form.employee_id} onChange={(e) => setField('employee_id', e.target.value)} placeholder="e.g. 340" />
              </div>
              <div className="space-y-1">
                <Label>Starts On</Label>
                <Input type="date" value={form.starts_on} onChange={(e) => setField('starts_on', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Ends On</Label>
                <Input type="date" value={form.ends_on} onChange={(e) => setField('ends_on', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Hourly Rate</Label>
                <Input type="number" value={form.hourly_rate} onChange={(e) => setField('hourly_rate', e.target.value)} placeholder="e.g. 1500" />
              </div>
              <div className="space-y-1">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setField('status', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Renewal State</Label>
                <Select value={form.renewal_state} onValueChange={(v) => setField('renewal_state', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>E-sign Status</Label>
                <Select value={form.esign_status} onValueChange={(v) => setField('esign_status', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unsigned">Unsigned</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="signed">Signed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Auto Renew</Label>
                <Select value={form.auto_renew ? 'yes' : 'no'} onValueChange={(v) => setField('auto_renew', v === 'yes')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </DialogFormGrid>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input value={form.notes || ''} onChange={(e) => setField('notes', e.target.value)} />
            </div>
          </DialogFormSection>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submit}>{editingId ? 'Save changes' : 'Add contract'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EducationContractsManagement;
